import { fmt } from "../lib/format";
import { GradeBadge } from "./GradeBadge";

const Bar = ({ c }) => (
  <div
    className={`flex items-center justify-between px-2 py-1 border-b border-zinc-800/70 ${c.active ? "" : "opacity-45"}`}
  >
    <div className="flex items-center gap-2 min-w-0">
      <span className={`h-1.5 w-1.5 rounded-full shrink-0 ${c.active ? "bg-blue-400" : "bg-zinc-700"}`} />
      <span className="text-[11px] text-zinc-300 truncate">{c.name}</span>
    </div>
    <div className="flex items-center gap-2 shrink-0">
      <span className="text-[9.5px] text-zinc-500 truncate max-w-[130px] hidden md:inline">{c.detail}</span>
      <span className={`font-mono text-[10px] font-semibold w-6 text-right ${c.active ? "text-blue-400" : "text-zinc-600"}`}>
        +{c.weight}
      </span>
    </div>
  </div>
);

export const ConfluencePanel = ({ ev, threshold, skipReason }) => {
  if (!ev) return <div className="p-3 text-xs text-zinc-600">Select a symbol</div>;
  const score = ev.score || 0;
  const hot = score >= threshold;
  const entering = /^ENTER|^Entered/i.test(skipReason || "");
  const awaiting = /await|confirm|MICRO mode/i.test(skipReason || "");
  const rowCls = entering ? "bg-green-500/10 border-green-500/30 text-green-300"
    : awaiting ? "bg-amber-500/10 border-amber-500/30 text-amber-300"
    : "bg-orange-500/10 border-orange-500/30 text-orange-300";
  return (
    <div className="flex flex-col h-full min-h-0">
      {skipReason && (
        <div data-testid="confluence-skip-reason" className={`flex items-center gap-2 px-3 py-1.5 border-b text-[10.5px] font-medium ${rowCls}`}>
          <span className="uppercase tracking-wide text-[8.5px] opacity-70 font-bold">{entering ? "Action" : "Agent status"}</span>
          <span className="truncate">{skipReason}</span>
        </div>
      )}
      <div className="flex items-center justify-between px-3 py-2 border-b border-zinc-800">
        <div className="flex items-center gap-2">
          <span className="font-mono text-sm font-bold text-zinc-100">{ev.symbol}</span>
          {ev.direction && (
            <span className={`px-1.5 py-0.5 text-[10px] font-bold rounded-sm ${ev.direction === "LONG" ? "text-green-500 bg-green-500/10" : "text-red-500 bg-red-500/10"}`}>
              {ev.direction}
            </span>
          )}
          <span className="text-[10px] text-zinc-500 font-mono">ATR {fmt(ev.atr)}</span>
          <GradeBadge grade={ev.grade} />
          {ev.rr != null && <span className="text-[10px] text-zinc-400 font-mono">{ev.rr}R</span>}
        </div>
        <div className="flex items-center gap-2">
          <span className={`font-mono text-lg font-bold ${hot ? "text-blue-400" : "text-zinc-300"}`}>{score}</span>
          <span className="text-[10px] text-zinc-500 font-mono">/ {threshold}</span>
          {hot && <span className="px-1.5 py-0.5 text-[9px] font-bold rounded-sm bg-blue-500/15 text-blue-400 border border-blue-500/30 animate-pulse">SETUP</span>}
        </div>
      </div>
      <div className="flex-1 min-h-0 overflow-auto thin-scroll">
        {(ev.components || []).map((c, i) => <Bar key={i} c={c} />)}
      </div>
      {ev.direction && ev.stop_loss != null && (
        <div className="grid grid-cols-3 border-t border-zinc-800 text-center">
          <div className="py-1.5 border-r border-zinc-800">
            <div className="text-[9px] uppercase text-zinc-500">Entry</div>
            <div className="font-mono text-[11px] text-zinc-100">{fmt(ev.price)}</div>
          </div>
          <div className="py-1.5 border-r border-zinc-800">
            <div className="text-[9px] uppercase text-zinc-500">Stop</div>
            <div className="font-mono text-[11px] text-red-500">{fmt(ev.stop_loss)}</div>
          </div>
          <div className="py-1.5">
            <div className="text-[9px] uppercase text-zinc-500">Target</div>
            <div className="font-mono text-[11px] text-blue-400">{fmt(ev.take_profit)}</div>
          </div>
        </div>
      )}
    </div>
  );
};
